import React, { useState, useEffect, useRef, useId } from 'react';
import Select from 'react-select';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import NewCommonMultiSelect from '../../Components/Utility/FilterAction/NewCommonMultiSelect';
import HtmlEditorModal from '../RangePlan/ProductList/RenderHtmlComponent/HtmlEditorModal';
import CustomDatePicker from '../../Components/Utility/DatePicker/DatePicker';
import { checkIsNumber, shortenandHoverString, customStyles } from '../../Components/helper';

const useNumberInput = (value, change) => {
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (checkIsNumber(inputValue)) {
      setLocalValue(inputValue);
    }
  };

  const handleBlur = () => {
    if (localValue !== value) {
      change(localValue);
    }
  };

  return {
    localValue,
    inputRef,
    handleChange,
    handleBlur,
  };
};

const useTextInput = (value, change) => {
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    if (localValue !== value) {
      change(localValue);
    }
  };

  return {
    localValue,
    inputRef,
    handleChange,
    handleBlur,
  };
};

function NumberInput({ value, change, subType, disableCondition }) {
  const { localValue, inputRef, handleChange, handleBlur } = useNumberInput(value, change);

  return (
    <input
      ref={inputRef}
      type="number"
      value={localValue}
      className="form-control text-bordered w-100"
      onKeyDown={(e) => {
        if (e?.keyCode === 110 && subType === 'integer') {
          e.preventDefault();
        }
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disableCondition}
    />
  );
}

NumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  change: PropTypes.func.isRequired,
  subType: PropTypes.string,
  disableCondition: PropTypes.bool,
};

NumberInput.defaultProps = {
  value: '',
  subType: '',
  disableCondition: false,
};

function NumberPercentageInput({ value, change, disableCondition }) {
  const { localValue, inputRef, handleChange, handleBlur } = useNumberInput(value, change);

  return (
    <div className="d-flex align-items-center w-100">
      <input
        ref={inputRef}
        type="number"
        value={localValue}
        className="form-control text-bordered w-100 mr-1"
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disableCondition}
      />
      %
    </div>
  );
}

NumberPercentageInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  change: PropTypes.func.isRequired,
  disableCondition: PropTypes.bool,
};

NumberPercentageInput.defaultProps = {
  value: '',
  disableCondition: false,
};

function TextAreaInput({ value, change, disableCondition, characterLimit, isFromPim, isTextAreaEditable }) {
  const { localValue, inputRef, handleChange, handleBlur } = useTextInput(value, change);

  if (!isTextAreaEditable && isFromPim) {
    return (
      <div className="text-field">
        <div className="text-field-input">{shortenandHoverString(value, 10, 'auto')}</div>
      </div>
    );
  }

  return (
    <textarea
      ref={inputRef}
      type="Text area"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className="form-control text-bordered flex-fill"
      disabled={disableCondition}
      maxLength={characterLimit}
    />
  );
}

TextAreaInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  change: PropTypes.func.isRequired,
  disableCondition: PropTypes.bool,
  characterLimit: PropTypes.number,
  isFromPim: PropTypes.bool,
  isTextAreaEditable: PropTypes.bool,
};

TextAreaInput.defaultProps = {
  value: '',
  disableCondition: false,
  characterLimit: undefined,
  isFromPim: false,
  isTextAreaEditable: false,
};

function RenderingManualItems({
  keyName,
  keyprop,
  value,
  placeholder,
  disableCondition,
  change,
  options,
  characterLimit,
  labelColours,
  subType,
  status,
  isFromPim,
  onCalendarOpen,
  onCalendarClose,
  onMenuClose,
  onMenuOpen,
  statusColoursOptions,
}) {
  let activeStatus;
  if (statusColoursOptions) {
    activeStatus = statusColoursOptions.find((ele) => {
      if (ele.setDefault === true) {
        return true;
      }
      if (value) {
        return ele._id === value?._id;
      }
      if (status?._id) {
        return ele._id === status?._id;
      }
      return ele.colourHexCode === status?.colourHexCode;
    });
  }

  const selectStyle = {
    ...customStyles,
    control: () => ({
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: '25px',
      border: '1px solid #848484',
      marginTop: '0',
      backgroundColor: `${activeStatus?.colourHexCode} !important`,
      color: '#FFF',
      padding: '0 5px',
    }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.7 : 1;
      return { ...provided, opacity, color: '#000' };
    },
    option: (styles, { data, isSelected }) => {
      const foundStatus = statusColoursOptions?.find((status) => status.colourName === data.colourName);
      return {
        ...styles,
        color: isSelected ? '#000' : `${foundStatus?.colourHexCode} !important`,
        backgroundColor: isSelected ? `${foundStatus?.colourHexCode} !important` : 'white',
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      alignItems: 'center',
    }),
  };
  const fieldInstanceId = useId();
  const [state, setState] = useState({
    activeHtmlEditor: false,
    isTextAreaEditable: false,
    isTextFieldEditable: false,
  });
  const { t } = useTranslation('common');

  const { activeHtmlEditor, isTextAreaEditable, isTextFieldEditable } = state;

  const toggle = () => {
    setState((prev) => ({ ...prev, activeHtmlEditor: false }));
  };

  const toggleTextArea = () => {
    setState((prev) => ({ ...prev, isTextAreaEditable: !state?.isTextAreaEditable }));
  };

  const toggleTextFields = () => {
    setState((prev) => ({ ...prev, isTextFieldEditable: !state?.isTextFieldEditable }));
  };
  function createMarkup(htl) {
    return {
      __html: htl,
    };
  }

  switch (keyName) {
    case 'Checkbox':
      return (
        <div>
          {options?.map((opt, ind) => {
            const checkboxId = `Checkbox_${fieldInstanceId}_${ind}`;
            return (
              <div className=" common-checkbox  mb-1 size-sm" key={`Checkbox_${keyprop}_${opt?._id}_${ind}`}>
                <input
                  type="checkbox"
                  onChange={(e) => change({ [ind]: e.target.checked })}
                  name={checkboxId}
                  id={checkboxId}
                  checked={get(value, `[${ind}]`, false)}
                  disabled={disableCondition}
                />
                <label htmlFor={checkboxId} className="chklabel">
                  <span>{opt?.name}</span>
                </label>
              </div>
            );
          })}
        </div>
      );
    case 'Date_picker': {
      const isValidValue = value && value !== '-' && (typeof value === 'string' ? value?.trim() !== '' : true);
      return (
        <CustomDatePicker
          keyProp={keyprop}
          dateFormat="dd-MMM-yyyy"
          valueProp={isValidValue ? value : null}
          name={keyprop}
          placeholder={placeholder}
          disable={disableCondition}
          onChange={(e) => change(e)}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
          isDateClearable
        />
      );
    }
    case 'HTML':
      return activeHtmlEditor ? (
        <HtmlEditorModal toggle={toggle} value={value || ''} handleChange={change} />
      ) : (
        <div className="w-100">
          {value && value?.length > 0 ? (
            <OverlayTrigger
              placement={t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.BUTTON')}
              trigger={['hover', 'focus']}
              rootClose
              overlay={
                <Popover className="custom-popover">
                  <p dangerouslySetInnerHTML={createMarkup(value || '')} />
                </Popover>
              }
            >
              <div className="d-flex align-items-center">
                <div className="paragraph-ellipsis" dangerouslySetInnerHTML={createMarkup(value || '')} />
                <button
                  type="button"
                  aria-label="edit"
                  className="btn p-0 px-1"
                  disabled={disableCondition}
                  onClick={() => setState((prev) => ({ ...prev, activeHtmlEditor: true }))}
                >
                  <span className="icon-Edit-1 f-24" />
                </button>
              </div>
            </OverlayTrigger>
          ) : (
            !disableCondition && (
              <button
                type="button"
                className="btn p-o px-1"
                aria-label="edit"
                disabled={disableCondition}
                onClick={() => setState((prev) => ({ ...prev, activeHtmlEditor: true }))}
              >
                <span className="icon-Edit-1 f-24" />
              </button>
            )
          )}
        </div>
      );
    case 'Dropdown':
      return (
        <div className="w-100 m-w-150 common-react-select mb-0">
          <Select
            name="Location"
            options={options}
            className="common-select"
            classNamePrefix="common-select"
            key={keyprop}
            placeholder={t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.CUSTOM_SECTIONS')}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?._id}
            value={options?.find((e) => e?._id === value || e?._id === value?._id) || (!value && options?.find((e) => e?.isDefault)) || null}
            onChange={(e) => change(e)}
            menuPosition="fixed"
            menuShouldBlockScroll
            menuShouldScrollIntoView
            isDisabled={disableCondition}
            isClearable
            onMenuOpen={onMenuOpen}
            onMenuClose={onMenuClose}
          />
        </div>
      );
    case 'Label': {
      const checkColours = labelColours?.filter((e) => e?.isLabelColourChecked);
      return (
        <div className="color-list">
          {checkColours?.map((colou, ind) => {
            return (
              <div key={ind}>
                <span
                  style={{
                    height: '15px',
                    width: '15px',
                    backgroundColor: colou?.colourHexCode,
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}
                />
                <label className="m-0 ml-2">{colou?.colourName}</label>
              </div>
            );
          })}{' '}
        </div>
      );
    }
    case 'Text_area':
      return (
        <div className="d-flex changes-pcp">
          <TextAreaInput
            value={value}
            change={change}
            disableCondition={disableCondition}
            characterLimit={characterLimit}
            isFromPim={isFromPim}
            isTextAreaEditable={isTextAreaEditable}
          />
          {isFromPim && !disableCondition && (
            <button type="button" aria-label="edit" onClick={toggleTextArea} className="btn p-0 px-1" disabled={disableCondition}>
              <i className="icon-Edit-1 f-24" />
            </button>
          )}
        </div>
      );
    case 'Multi_select_Dropdown': {
      return (
        <div className="w-100 m-w-150">
          <NewCommonMultiSelect
            name="MultiSelect"
            optionList={options}
            className="custom-multiselect"
            selectLabel="name"
            selectValue="_id"
            selectedValue={value}
            onChange={(selected) => change(selected)}
            dataPlaceholder={t('PRODUCT_CRITICAL_PATH.RENDERING_MANUALITEMS.SELECT_MULTIPLE_OPTIONS')}
            disable={disableCondition}
            marginBottomZero
            hideTitle
            onMenuOpen={onMenuOpen}
            onMenuClose={onMenuClose}
          />
        </div>
      );
    }
    case 'Number_Decimal':
    case 'Number_Integer':
      return <NumberInput value={value} change={change} subType={subType} disableCondition={disableCondition} />;
    case 'Number_percentage':
      return <NumberPercentageInput value={value} change={change} disableCondition={disableCondition} />;
    case 'Radio_button':
      return (
        <div className="d-flex align-items-center flex-wrap w-100">
          {options?.map((opt, i) => {
            return (
              <div className="custom-radio mr-2 my-1" key={i}>
                <input
                  type="radio"
                  id={`raio_${keyprop}_${new Date().valueOf()}_${opt?._id}`}
                  value={opt?._id}
                  onChange={(e) => change(e?.target?.value)}
                  checked={value === opt?._id}
                  disabled={disableCondition}
                />
                <label className="mb-0" htmlFor={`raio_${keyprop}_${new Date().valueOf()}_${opt?._id}`}>
                  {shortenandHoverString(opt?.name, 10, 'auto')}
                  &nbsp;
                </label>
              </div>
            );
          })}
        </div>
      );
    case 'Status':
      return (
        <div className="common-react-select mb-0 w-100">
          <Select
            options={statusColoursOptions}
            value={value || status}
            inputProps={{
              autoComplete: 'off',
              autoCorrect: 'off',
              spellCheck: 'off',
            }}
            getOptionLabel={(option) => `${option.colourName}`}
            getOptionValue={(option) => `${option._id}`}
            className="common-select"
            classNamePrefix="common-select"
            styles={selectStyle}
            onChange={(e) => change(e)}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
          />
        </div>
      );
    case 'Textbox': {
      const pimTextBox = !isTextFieldEditable ? (
        <div className="text-field">
          <div className="text-field-input">{shortenandHoverString(value, 10, 'auto')}</div>
        </div>
      ) : (
        <div className="common-input-control  no-label mb-0">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => change(e?.target?.value)}
            className="form-control text-bordered"
            disabled={disableCondition}
            maxLength={Number(characterLimit) > 0 ? characterLimit : undefined}
          />
          {Number(characterLimit) > 0 ? (
            <div className="text-count">
              {value?.length ?? 0}/{characterLimit}
            </div>
          ) : null}
        </div>
      );
      return (
        <div className="d-flex">
          {isFromPim ? (
            pimTextBox
          ) : (
            <div className="d-flex flex-column flex-fill">
              <input
                type="text"
                value={value || ''}
                onChange={(e) => change(e?.target?.value)}
                className="form-control text-bordered"
                disabled={disableCondition}
                maxLength={characterLimit}
              />
              <div className="text-count">
                {value?.length || 0}/{characterLimit}
              </div>
            </div>
          )}
          {isFromPim && !disableCondition && (
            <button type="button" onClick={toggleTextFields} aria-label="edit" className="btn p-0 px-1" disabled={disableCondition}>
              <i className="icon-Edit-1 f-24" />
            </button>
          )}
        </div>
      );
    }
    case 'Time_picker':
      return (
        <CustomDatePicker
          keyProp={keyprop}
          dateFormat="dd-MMM-yyyy"
          valueProp={value || null}
          name={keyprop}
          placeholder={placeholder}
          disable={disableCondition}
          onChange={change}
          showTime
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
        />
      );

    default:
      return null;
  }
}

RenderingManualItems.propTypes = {
  keyName: PropTypes.string.isRequired,
  keyprop: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object]),
  placeholder: PropTypes.string,
  disableCondition: PropTypes.bool,
  change: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  characterLimit: PropTypes.number,
  labelColours: PropTypes.arrayOf(
    PropTypes.shape({
      isLabelColourChecked: PropTypes.bool,
      colourHexCode: PropTypes.string,
      colourName: PropTypes.string,
    }),
  ),
  subType: PropTypes.string,
  status: PropTypes.shape({
    _id: PropTypes.string,
    colourHexCode: PropTypes.string,
    colourName: PropTypes.string,
  }),
  isFromPim: PropTypes.bool,
  onCalendarOpen: PropTypes.func,
  onCalendarClose: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  statusColoursOptions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      colourHexCode: PropTypes.string,
      colourName: PropTypes.string,
      setDefault: PropTypes.bool,
    }),
  ),
};

RenderingManualItems.defaultProps = {};

export default React.memo(RenderingManualItems);
